import React, { memo } from 'react';
import { ExternalLink } from 'lucide-react';
import { Highlight } from './Highlight';
import { Badge } from '../ui/Badge';
import { cx } from '../../lib/cx';

export const ROW_FACTS = [
  ['specimen', 'Specimen'],
  ['container', 'Container'],
  ['volume', 'Volume'],
  ['stability', 'Stability'],
];

/** Where the test is done, when the source names a (short) site or department. */
export const testingSite = (model) => {
  const site = model.facts.performedAt || '';
  return site && site.length <= 48 && site !== model.labName ? site : '';
};

export const labLine = (model, { includeLab = true } = {}) =>
  [
    includeLab && model.labName,
    model.subLab,
    testingSite(model),
    includeLab && model.region && model.region !== 'Custom' ? model.region : '',
  ].filter(Boolean).join(' · ');

/** The 2–4 facts that matter most when choosing a tube, or the synonyms. */
export const FactLine = ({ model, highlighter }) => {
  const facts = ROW_FACTS.filter(([key]) => model.facts[key]);
  if (!facts.length) {
    if (!model.aliases.length) return null;
    return (
      <span className="line-clamp-1 text-xs text-muted">
        <span className="text-faint">Also known as </span>
        <Highlight text={model.aliases.slice(0, 6).join(', ')} highlighter={highlighter} />
      </span>
    );
  }
  return (
    // Wraps to at most two lines on phones; one line of truncated facts on wider screens.
    <span className="line-clamp-2 text-xs text-fg-2 sm:flex sm:flex-wrap sm:gap-x-4 sm:gap-y-0.5">
      {facts.map(([key, label]) => (
        <span key={key} className="mr-3 sm:mr-0 sm:min-w-0 sm:max-w-[22rem] sm:truncate">
          <span className="text-faint">{label} </span>
          <Highlight text={model.facts[key]} highlighter={highlighter} />
        </span>
      ))}
    </span>
  );
};

/**
 * One result. The main button opens the detail pane; the separate link
 * opens the lab's own page (two distinct, labelled targets).
 */
export const ResultRow = memo(function ResultRow({ model, highlighter, selected, onOpen, showLab = true }) {
  return (
    <li className="relative flex items-stretch border-b border-line last:border-b-0">
      {selected && <span className="absolute inset-y-0 left-0 w-0.5 bg-accent" aria-hidden="true" />}
      <button
        type="button"
        data-row-button
        data-result-id={model.id}
        onClick={() => onOpen(model.id)}
        aria-current={selected ? 'true' : undefined}
        className={cx(
          'flex min-w-0 flex-1 flex-col gap-[var(--row-gap)] px-4 py-[var(--row-py)] text-left transition-colors focus-visible:-outline-offset-2',
          selected ? 'bg-accent/10' : 'hover:bg-raised/60',
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-medium text-fg">
            <Highlight text={model.titleText} highlighter={highlighter} />
          </span>
          {model.referral === 'referred' && (
            <Badge tone="accent" title={`Referred to ${model.facts.referredTo}`}>Referred</Badge>
          )}
        </span>
        {(showLab || labLine(model, { includeLab: false })) && (
          <span className="truncate text-xs text-muted">{labLine(model, { includeLab: showLab })}</span>
        )}
        <FactLine model={model} highlighter={highlighter} />
      </button>
      {model.link && (
        <a
          href={model.link}
          target="_blank"
          rel="noopener noreferrer"
          referrerPolicy="no-referrer"
          className="flex w-11 shrink-0 items-center justify-center text-faint transition-colors hover:bg-raised/60 hover:text-accent-fg"
          aria-label={`Open ${model.titleText} on the ${model.labName} website (new tab)`}
          title="Open on the lab's website"
        >
          <ExternalLink size={15} aria-hidden="true" />
        </a>
      )}
    </li>
  );
});

/**
 * Arrow-key (and j/k) navigation between the row buttons inside the element
 * the handler is attached to. With `onFollow`, the newly focused row is also
 * opened (used while the docked detail pane is showing a result).
 */
export const handleRowKeyNav = (e, onFollow = null) => {
  const keys = { ArrowDown: 1, ArrowUp: -1, j: 1, k: -1 };
  const delta = keys[e.key];
  if (!delta || e.altKey || e.ctrlKey || e.metaKey) return;
  const target = e.target;
  if (!target.matches || !target.matches('[data-row-button]')) return;
  const rows = Array.from(e.currentTarget.querySelectorAll('[data-row-button]'));
  const next = rows[rows.indexOf(target) + delta];
  if (next) {
    e.preventDefault();
    next.focus();
    if (onFollow && next.dataset.resultId) onFollow(next.dataset.resultId);
  }
};
